/**
 * useFirstLoginBrainRedirect — Milestone 37.4 · Phase 2 correction.
 *
 * Locked spec:
 *
 *   When an authenticated founder enters a workspace and no approved
 *   Business Brain exists:
 *     1. Automatically route them to /business-brain.
 *     2. Show the Business Brain welcome screen.
 *     3. Present two clear actions — Continue Setup / Explore Orkexra First.
 *
 *   If they choose Explore, the deferral is *persisted* so ordinary
 *   navigation does not repeatedly force-redirect during the same
 *   setup state, but the persistent Brain Setup tile remains on
 *   Executive Home.
 *
 * The **server** is the source of truth (``/brain-missions/gate``).
 * Local storage is a flicker-prevention hint only:
 *
 *   * approved brain in server response → NEVER redirect
 *   * not approved AND no deferral flag → redirect once
 *   * not approved AND deferral flag    → do not redirect; show tile
 *
 * Deferral is scoped per workspace so switching workspaces doesn't
 * inherit another workspace's deferral.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useBrainGate } from "@/entities/brain-missions";
import { useCurrentWorkspace } from "@/entities/workspace";

/** Storage-key helper. Kept internal so the shape can be evolved without leaking. */
export function brainDeferralKey(workspaceId: string): string {
  return `cz.brain-setup.deferred.${workspaceId}`;
}

/** Explicit deferral setter. Called by the WelcomeScreen's Explore CTA. */
export function persistBrainDeferral(workspaceId: string): void {
  try {
    window.localStorage.setItem(brainDeferralKey(workspaceId), "1");
  } catch {
    /* localStorage may be unavailable — deferral falls back to session-only */
  }
}

/** Read the deferral state. Used by tests and callers that need a boolean. */
export function readBrainDeferral(workspaceId: string): boolean {
  try {
    return window.localStorage.getItem(brainDeferralKey(workspaceId)) === "1";
  } catch {
    return false;
  }
}

/**
 * Mounted from ``HomePage``. Fires exactly once per Home mount when
 * every precondition is met, then navigates *replace* so the founder's
 * Back button doesn't bounce them back to Home mid-redirect.
 */
export function useFirstLoginBrainRedirect(): void {
  const ws = useCurrentWorkspace();
  const gateQ = useBrainGate(ws?.id);
  const navigate = useNavigate();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!ws?.id) return;
    if (!gateQ.data) return;
    // Server is source of truth.
    if (gateQ.data.business_brain_approved) return;
    // Founder explicitly deferred setup — respect it.
    if (readBrainDeferral(ws.id)) return;
    // Public preview / guest → never redirect (guests don't hit /home).
    // Demo workspace with a pre-approved BB is caught by the gate check
    // above, so no extra branch needed.
    firedRef.current = true;
    navigate("/business-brain", { replace: true });
  }, [ws?.id, gateQ.data?.business_brain_approved, navigate, gateQ.data]);
}
